import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, UserCircle, ChevronRight, Menu, ArrowLeftFromLine } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { defaultAvatarUrl } from "@/lib/avatar";
import { TopicSearchBar } from "@/pages/atlas/AtlasTopicSearchBar";

// Same brand asset the traveler-side AppShell renders — but the "Travel"
// letterforms are recolored navy so the mark reads correctly against
// the WHITE Atlas header background (the traveler AppShell uses a navy
// header, where the default white-Travel PNG is perfect). Generated
// once from /assets/logo-landscape.png by shifting near-white pixels
// to #1a3a5f (tb-navy) — see PRD note 2026-07-29.
const LOGO_URL = "/assets/logo-landscape-navy.png";

/**
 * Atlas top bar — TravelBio logo + "Advisor Atlas" subtitle on the left,
 * breadcrumb / current page title in the middle, profile menu on the
 * right (avatar + name + "Advisor" chip + optional "Admin" chip + sign out).
 * White background, upright headers, no italic.
 */

const CRUMB_MAP = {
  dashboard: "Dashboard",
  search: "Search",
  trips: "Trips",
  clients: "Clients",
  knowledge: "Knowledge",
  new: "New entry",
  playbook: "Playbook",
  training: "Training",
  promotions: "Promotions",
  commissions: "Commissions",
  "rewards-wallet": "Rewards Wallet",
  messages: "Messages",
  reports: "Reports",
  settings: "Settings",
  "ai-assistant": "AI Assistant",
  support: "Support",
  admin: "Admin",
  advisors: "Advisors",
  "review-queue": "Review Queue",
};

function useBreadcrumb() {
  const { pathname } = useLocation();
  return useMemo(() => {
    const parts = pathname.replace(/^\/+|\/+$/g, "").split("/").slice(1);
    if (parts.length === 0) return ["Dashboard"];
    return parts.map(p => CRUMB_MAP[p] || p.replace(/-/g, " "));
  }, [pathname]);
}

export default function AtlasHeader({ isAdmin = false, onOpenDrawer }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const crumbs = useBreadcrumb();
  const avatar = user?.avatar_url || defaultAvatarUrl(user?.email || "");
  // Msg 563 · D.5 — the topic search is prominent on /atlas/topics
  // landing itself; suppress the compact header variant on that
  // route to avoid visual redundancy.
  const hideHeaderSearch = location.pathname === "/atlas/topics";

  const doSignOut = async () => {
    await logout();
    nav("/", { replace: true });
  };

  return (
    <header
      className="shrink-0 border-b border-tb-navy/15 bg-white flex items-center justify-between gap-2 px-3 md:px-6"
      style={{ height: 84 }}
      data-testid="atlas-header"
    >
      {/* Msg 555 — mobile hamburger. Renders only below md; taps open
          the sidebar drawer wired up in AtlasLayout. */}
      {onOpenDrawer && (
        <button
          type="button"
          onClick={onOpenDrawer}
          aria-label="Open navigation"
          data-testid="atlas-header-menu"
          className="md:hidden inline-flex items-center justify-center w-10 h-10 -ml-1 rounded-full text-tb-navy hover:bg-tb-navy/5 shrink-0"
        >
          <Menu className="w-5 h-5" strokeWidth={2} />
        </button>
      )}
      {/* Left — logo mark + Advisor Atlas subtitle */}
      <button
        type="button"
        onClick={() => nav("/atlas/dashboard")}
        className="flex flex-col items-start gap-0 leading-none tb-tap select-none min-w-0"
        aria-label="Atlas home"
        data-testid="atlas-header-logo"
      >
        <img
          src={LOGO_URL}
          alt="TravelBio"
          width={156}
          height={56}
          draggable={false}
          className="block select-none"
          style={{ height: 56, width: "auto", objectFit: "contain" }}
        />
        <div
          className="text-[10px] tracking-[0.25em] uppercase text-tb-navy/55 font-hand -mt-0.5 pl-1"
          data-testid="atlas-header-subtitle"
        >
          Advisor Atlas
        </div>
      </button>

      {/* "Back to TravelBio" — jump from the advisor Atlas back into
          the traveler experience. Uses the same outline-pill treatment
          as Sign out so it reads as a secondary control, not primary
          navigation. Icon-only on narrow viewports. */}
      <button
        type="button"
        onClick={() => nav("/explore")}
        data-testid="atlas-back-to-travelbio"
        aria-label="Back to TravelBio"
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-tb-navy/20 text-tb-navy/75 hover:bg-tb-navy hover:text-white font-hand text-xs tb-tap transition-colors shrink-0"
      >
        <ArrowLeftFromLine className="w-3.5 h-3.5" strokeWidth={2} />
        <span className="hidden md:inline">Back to TravelBio</span>
      </button>

      {/* Middle — breadcrumb (compact) + persistent topic search bar */}
      <div className="hidden md:flex flex-1 items-center gap-4 min-w-0">
        <nav
          aria-label="Breadcrumb"
          className="hidden xl:flex items-center gap-1.5 text-sm text-tb-navy/70 shrink"
          data-testid="atlas-header-breadcrumb"
        >
          {crumbs.map((c, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-tb-navy/30" />}
              <span
                className={
                  "font-hand " +
                  (i === crumbs.length - 1
                    ? "text-tb-navy font-medium"
                    : "text-tb-navy/55")
                }
              >
                {c}
              </span>
            </React.Fragment>
          ))}
        </nav>
        <div className="flex-1 max-w-md">
          {!hideHeaderSearch && (
            <TopicSearchBar
              size="md"
              testId="atlas-header-topic-search"
            />
          )}
        </div>
      </div>

      {/* Right — profile menu */}
      <div className="flex items-center gap-2 md:gap-3">
        <div className="flex items-center gap-2 md:gap-2.5">
          <div className="w-9 h-9 rounded-full overflow-hidden border border-tb-navy/20 bg-white">
            {user?.avatar_url ? (
              <img src={avatar} alt="" className="w-full h-full object-cover" />
            ) : (
              <UserCircle className="w-full h-full text-tb-navy/50" strokeWidth={1.4} />
            )}
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span
              className="text-tb-navy text-sm font-medium truncate max-w-[160px]"
              data-testid="atlas-header-user-name"
            >
              {user?.name || user?.email}
            </span>
            <div className="flex items-center gap-1 mt-0.5">
              <span
                className="text-[9px] px-1.5 py-0.5 rounded-full bg-tb-navy/5 border border-tb-navy/20 text-tb-navy/75 font-hand uppercase tracking-widest"
                data-testid="atlas-header-role-advisor"
              >
                Advisor
              </span>
              {isAdmin && (
                <span
                  className="text-[9px] px-1.5 py-0.5 rounded-full bg-tb-gold/20 border border-tb-gold text-tb-navy font-hand uppercase tracking-widest"
                  data-testid="atlas-header-role-admin"
                >
                  Admin
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={doSignOut}
          data-testid="atlas-sign-out"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-tb-navy/20 text-tb-navy/75 hover:bg-tb-navy hover:text-white font-hand text-xs tb-tap transition-colors"
          aria-label="Sign out"
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={2} />
          <span className="hidden sm:inline">Sign out</span>
        </button>
      </div>
    </header>
  );
}
